import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogFooter } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { formatCZK, plur } from "@/lib/format";
import { Wallet, Package, PackageX, FileInput, Banknote, Trash2, Link2, HardHat } from "lucide-react";
import { toast } from "sonner";

// Druh dokladu → REST základ. Přijímá jak řeč seznamu (faktura/uctenka/vydana),
// tak backendové názvy (document/receipt/issued).
const EP = {
  faktura: "documents", document: "documents",
  uctenka: "receipts", receipt: "receipts",
  vydana: "issued-invoices", issued: "issued-invoices",
};
const ISSUED = new Set(["vydana", "issued"]);
const fmtQty = (n) => Number(n || 0).toLocaleString("cs-CZ");

// Potvrzení smazání dokladu s výčtem dopadů (zadání 160). Řízené přes `target`
// = { kind, id }; při otevření natáhne /delete-impact. Když jsou vazby, žádá
// zaškrtnutí „Rozumím…". Používá se u koše v seznamu, v patičce Kontroly dokladu
// i u detailu účtenky — všude místo window.confirm.
export function DeleteDocumentDialog({ target, onClose, onDeleted }) {
  const [impact, setImpact] = useState(null);
  const [loading, setLoading] = useState(false);
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const kind = target?.kind;
  const base = EP[kind];
  const issued = ISSUED.has(kind);
  const noun = issued ? "fakturu" : "doklad";

  useEffect(() => {
    if (!target) { setImpact(null); setAck(false); return; }
    setLoading(true); setImpact(null); setAck(false);
    api.get(`/${base}/${target.id}/delete-impact`)
      .then((r) => setImpact(r.data))
      .catch(() => setImpact({ label: "" }))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  const linked = !!(impact && (impact.payments || impact.stocked || impact.stock_queue_pending || impact.sub_invoice || impact.cash_case || impact.advances || impact.settled_by));

  const doDelete = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete(`/${base}/${target.id}`);
      const n = data?.stocked?.lines;
      toast.success(n
        ? `${issued ? "Faktura smazána" : "Doklad smazán"} · odepsáno ze skladu ${plur(n, "položka", "položky", "položek")}`
        : (issued ? "Faktura smazána" : "Doklad smazán"));
      onDeleted?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Smazání selhalo.");
    } finally { setBusy(false); }
  };

  return (
    <AlertDialog open={!!target} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <AlertDialogContent data-testid="delete-doc-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>{linked ? "Smazat doklad včetně vazeb?" : `Smazat ${noun}?`}</AlertDialogTitle>
        </AlertDialogHeader>

        {loading ? (
          <div className="space-y-2" data-testid="delete-impact-loading">
            <div className="h-4 w-2/3 rounded bg-muted animate-pulse" />
            <div className="h-4 w-1/2 rounded bg-muted animate-pulse" />
          </div>
        ) : linked ? (
          <div className="space-y-3 text-sm">
            {impact.label && <div className="font-semibold text-foreground">{impact.label}</div>}
            <ul className="space-y-2.5" data-testid="delete-impact-list">
              {impact.payments && (
                <li className="flex gap-2.5"><Wallet size={17} className="shrink-0 mt-0.5 text-brand" />
                  <span>Zmizí {plur(impact.payments.count, "úhrada", "úhrady", "úhrad")} · {formatCZK(impact.payments.sum)}</span></li>
              )}
              {impact.stocked && (
                <li className="flex gap-2.5"><Package size={17} className="shrink-0 mt-0.5 text-brand" />
                  <div className="min-w-0">
                    <div>Ze skladu se odepíše {plur(impact.stocked.lines, "naskladněná položka", "naskladněné položky", "naskladněných položek")}:</div>
                    <ul className="mt-1 space-y-1 text-muted-foreground">
                      {impact.stocked.items.map((it, i) => (
                        <li key={i}>
                          {it.name} · {fmtQty(it.qty)} {it.unit}{it.warehouse_name ? ` · ${it.warehouse_name}` : ""}
                          {it.issued_qty > 0 && (
                            <span className="text-amber-600 dark:text-amber-400"> — už vydáno {fmtQty(it.issued_qty)} {it.unit}{it.short > 0 ? `, sklad půjde do minusu o ${fmtQty(it.short)} ${it.unit}` : ""}</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div></li>
              )}
              {/* 406: vydaný materiál zůstává v nákladech zakázek — smazání dokladu výdej nevrací */}
              {impact.issued_to_projects && (
                <li className="flex gap-2.5" data-testid="delete-impact-issued"><HardHat size={17} className="shrink-0 mt-0.5 text-amber-600 dark:text-amber-400" />
                  <div className="min-w-0">
                    <div>Materiál z dokladu už je vydaný na {impact.issued_to_projects.projects.length > 1 ? "zakázky" : "zakázku"} a v {impact.issued_to_projects.projects.length > 1 ? "jejich" : "jejích"} nákladech zůstane:</div>
                    <ul className="mt-1 space-y-1 text-muted-foreground">
                      {impact.issued_to_projects.projects.map((p) => (
                        <li key={p.project_id} className="flex justify-between gap-3">
                          <span className="truncate">{p.name}</span>
                          <span className="tabular-nums whitespace-nowrap">{formatCZK(p.value)}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-1 text-xs text-muted-foreground">Byl-li doklad omylem, vraťte materiál nejdřív na sklad: Sklad › Pohyby › Vratka u výdeje.</div>
                  </div></li>
              )}
              {impact.stock_queue_pending > 0 && (
                <li className="flex gap-2.5"><PackageX size={17} className="shrink-0 mt-0.5 text-amber-600 dark:text-amber-400" />
                  <span>{plur(impact.stock_queue_pending, "položka čeká", "položky čekají", "položek čeká")} na naskladnění — zruší se</span></li>
              )}
              {impact.sub_invoice && (
                <li className="flex gap-2.5"><FileInput size={17} className="shrink-0 mt-0.5 text-brand" />
                  <span>Faktura subdodavatele {impact.sub_invoice.user_name} ({formatCZK(impact.sub_invoice.amount)}) se vrátí ke schválení</span></li>
              )}
              {/* Zálohy a vyúčtování (171) */}
              {impact.advances && (
                <li className="flex gap-2.5" data-testid="delete-impact-advances"><Link2 size={17} className="shrink-0 mt-0.5 text-brand" />
                  <span>{plur(impact.advances.count, "odečtená záloha se vrátí", "odečtené zálohy se vrátí", "odečtených záloh se vrátí")} mezi nevyúčtované ({formatCZK(impact.advances.sum)})</span></li>
              )}
              {impact.settled_by && (
                <li className="flex gap-2.5" data-testid="delete-impact-settled"><Link2 size={17} className="shrink-0 mt-0.5 text-amber-600 dark:text-amber-400" />
                  <span>Záloha je odečtená ve faktuře č. {impact.settled_by.number || "—"} — tam se k úhradě zvýší o {formatCZK(impact.settled_by.sum)}</span></li>
              )}
              {impact.cash_case && (
                <li className="flex gap-2.5"><Banknote size={17} className="shrink-0 mt-0.5 text-brand" />
                  <span>Účtenka se odpojí z případu pokladny {impact.cash_case.name}</span></li>
              )}
            </ul>
            <label htmlFor="delete-ack" className="flex items-center gap-2 pt-1 cursor-pointer font-semibold select-none">
              <Checkbox id="delete-ack" checked={ack} onCheckedChange={(v) => setAck(!!v)} data-testid="delete-ack" />
              Rozumím, chci doklad smazat
            </label>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground" data-testid="delete-impact-label">{impact?.label}</p>
        )}

        <AlertDialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="delete-cancel" className="h-11 w-full sm:w-auto">Zpět</Button>
          <Button variant="destructive" onClick={doDelete} disabled={busy || loading || (linked && !ack)}
            data-testid="delete-confirm" className="h-11 w-full sm:w-auto font-bold">
            <Trash2 size={16} /> {linked ? "Smazat včetně vazeb" : "Smazat"}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
